import { useEffect, useMemo, useRef } from "react";
import { message } from "antd";
import { useQuery } from "@tanstack/react-query";
import { GetOrderReturnById } from "../../Api";
import { useSystemConfig } from "../../Hook/useSystemConfig";
import { DEFAULT_STALE_TIME } from "../../Constant";

type ReturnConfig = {
  store_name: string;
  address: string;
  phone: string;
  currency: string;
  receipt_footer: string;
  email: string;
  whatsapp: string;
  website: string;
  logo_url: string;
};
type ReturnRecord = any;

interface UseReturnInvoiceDataParams {
  open: boolean;
  returnId: string | null;
}

const returnInvoiceQueryKeys = {
  detail: (returnId: string | null) =>
    ["returnInvoice", "detail", returnId] as const,
};

const fetchReturn = async (returnId: string): Promise<ReturnRecord> => {
  const res = await GetOrderReturnById(returnId);
  if (!res?.success) {
    throw new Error(res?.message || "Failed to load order return details.");
  }
  return res.data;
};

export const useReturnInvoiceData = ({
  open,
  returnId,
}: UseReturnInvoiceDataParams) => {
  const {
    refresh: refreshSystemConfig,
    storeName,
    address,
    phone,
    currency,
    receiptFooter,
    email,
    whatsapp,
    get,
  } = useSystemConfig();
  const prevOpenRef = useRef(open);

  useEffect(() => {
    if (open && !prevOpenRef.current) {
      refreshSystemConfig();
    }
    prevOpenRef.current = open;
  }, [open, refreshSystemConfig]);

  const query = useQuery<ReturnRecord, Error>({
    queryKey: returnInvoiceQueryKeys.detail(returnId),
    enabled: open && Boolean(returnId),
    staleTime: DEFAULT_STALE_TIME,
    queryFn: () => fetchReturn(returnId || ""),
  });

  const config: ReturnConfig = useMemo(
    () => ({
      store_name: storeName,
      address,
      phone,
      currency,
      receipt_footer: receiptFooter,
      email,
      whatsapp,
      website: (get("website") as string) || "",
      logo_url: (get("app_logo") as string) || "",
    }),
    [storeName, address, phone, currency, receiptFooter, email, whatsapp, get],
  );

  useEffect(() => {
    if (query.error) {
      message.error(query.error.message);
    }
  }, [query.error]);

  return {
    ...query,
    orderReturn: query.data ?? null,
    config,
  };
};
